import React, { useState } from "react";
import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { studentsApi } from "../../services/students/studentsApi";
import { Message } from "primereact/message";
import { studentDocumentsApi } from "../../services/studentDocuments/studentDocumentsApi";
import FilePreviewDialog from "../../components/common/FilePreviewDialog";
import { StudentInfoAdminResponse } from "../../models/dtos";

const ActionButtons = ({ student }: { student?: StudentInfoAdminResponse }) => {
  const [generateStudentEmail, { isLoading: isGenerateStudentEmailLoading, isError: isGenerateStudentEmailError }] =
    studentsApi.useGenerateStudentEmailMutation();
  const [unenrollStudent, { isLoading: isUnenrollingStudentLoading, isError: isUnenrollingStudentError }] =
    studentsApi.useUnenrollStudentMutation();
  const [sendDataToNacid, { isLoading: isSendingDataToNacidLoading, isError: isSendingDataToNacidError }] =
    studentsApi.useSendDataToNacidMutation();
  const [sendInfoEmail, { isLoading: isSendInfoEmailLoading, isError: isSendInfoEmailError }] =
    studentsApi.useSendInfoEmailMutation();

  const [downloadStudentDocumentProfile] = studentDocumentsApi.useLazyDownloadStudentDocumentProfileQuery();
  const [downloadDormitoryDeclaration] = studentDocumentsApi.useLazyDownloadDormitoryDeclarationQuery();
  const [downloadEducationContract] = studentDocumentsApi.useLazyDownloadEducationContractQuery();
  const [downloadHealthInsuranceDeclaration] = studentDocumentsApi.useLazyDownloadHealthInsuranceDeclarationQuery();
  const [downloadMainBook] = studentDocumentsApi.useLazyDownloadMainBookQuery();
  const [downloadStudentChecklist] = studentDocumentsApi.useLazyDownloadStudentChecklistQuery();
  const [generateStudentDiploma] = studentDocumentsApi.useGenerateStudentDiplomaMutation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");

  const openPdfInModal = async (downloadFunction: any, title: string) => {
    try {
      const result = await downloadFunction({ studentId: student?.studentId }).unwrap();
      console.log("Downloaded PDF:", result);
      const blob = new Blob([result.blob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(title);
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const generateDiploma = async () => {
    try {
      const request = {
        studentId: student?.studentId!,
        documentNumber: "",
        documentNote: "",
        isEnglish: false,
      };
      const result = await generateStudentDiploma(request).unwrap();
      console.log("Generated diploma:", result);
      const blob = new Blob([result.blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating diploma:", error);
    }
  };

  const closePdfDialog = () => {
    setShowPdfDialog(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  if (!student) {
    return null;
  }

  return (
    <div className="flex flex-column gap-2">
      <label className="text-sm font-bold">Действия</label>
      <div>
        {isGenerateStudentEmailError && (
          <Message severity="error" text="Възникна грешка при генерирането на студентски и-мейл." />
        )}
        {isUnenrollingStudentError && <Message severity="error" text="Възникна грешка при отписването на студента." />}
        {isSendingDataToNacidError && (
          <Message severity="error" text="Възникна грешка при изпращането на данни към НАЦИД." />
        )}
        {isSendInfoEmailError && (
          <Message severity="error" text="Възникна грешка при изпращането на информативен и-мейл." />
        )}
      </div>
      <div className="flex flex-row gap-2">
        {!student?.userEmail && (
          <Button
            icon="pi pi-envelope"
            iconPos="top"
            size="small"
            className="flex-1"
            severity="secondary"
            outlined
            label="Генериране на студентски и-мейл"
            loading={isGenerateStudentEmailLoading}
            onClick={() => generateStudentEmail(student?.studentId)}
          />
        )}
        {student?.userEmail && (
          <Button
            icon="pi pi-envelope"
            iconPos="top"
            size="small"
            className="flex-1"
            severity="secondary"
            outlined
            label="Изпращане на информативен и-мейл"
            loading={isSendInfoEmailLoading}
            onClick={() => sendInfoEmail(student?.studentId)}
          />
        )}

        <Button
          icon="pi pi-upload"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Изпращане към НАЦИД"
          loading={isSendingDataToNacidLoading}
          onClick={() => sendDataToNacid(student?.studentId)}
        />

        <Button
          icon="pi pi-times"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="danger"
          outlined
          label="Отписване на студент"
          loading={isUnenrollingStudentLoading}
          onClick={(e) => {
            confirmPopup({
              target: e.currentTarget,
              message: "Сигурни ли сте, че искате да отпишете студента?",
              icon: "pi pi-exclamation-triangle",
              acceptClassName: "p-button-danger",
              accept: () => {
                unenrollStudent(student?.studentId);
              },
            });
          }}
        />
      </div>

      <label className="text-sm font-bold">Документи</label>
      <div className="flex flex-row gap-2">
        <Button
          icon="pi pi-file-pdf"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Именник"
          onClick={() => openPdfInModal(downloadStudentDocumentProfile, "Именник")}
        />

        <Button
          icon="pi pi-book"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="contrast"
          outlined
          label="Главна книга"
          onClick={() => openPdfInModal(downloadMainBook, "Главна книга")}
        />

        <Button
          icon="pi pi-file-pdf"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Декларация за общежитие"
          onClick={() => openPdfInModal(downloadDormitoryDeclaration, "Декларация за общежитие")}
        />

        <Button
          icon="pi pi-file-pdf"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Договор"
          onClick={() => openPdfInModal(downloadEducationContract, "Договор за обучение")}
        />

        <Button
          icon="pi pi-file-pdf"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Декларация за здравно осигуряване"
          onClick={() => openPdfInModal(downloadHealthInsuranceDeclaration, "Декларация за здравно осигуряване")}
        />

        <Button
          icon="pi pi-list"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Чеклист"
          onClick={() => openPdfInModal(downloadStudentChecklist, "Студентски чеклист")}
        />

        <Button
          icon="pi pi-file-excel"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="success"
          outlined
          label="Диплома"
          onClick={generateDiploma}
        />
      </div>

      <ConfirmPopup />

      <FilePreviewDialog visible={showPdfDialog} onHide={closePdfDialog} fileUrl={pdfUrl} title={pdfTitle} />
    </div>
  );
};

export default ActionButtons;
